import { SpanKind, SpanStatusCode, trace } from "@opentelemetry/api"

// Get the default tracer
const tracer = trace.getTracer("egged-website-client")

export interface GraphQLOperation {
  operationName?: string
  query: string
  variables?: Record<string, any>
}

export interface TelemetryOptions {
  userId?: string
  sessionId?: string
  customProperties?: Record<string, any>
}

/**
 * Track GraphQL operations with detailed telemetry
 */
export function trackGraphQLOperation<T>(
  operation: GraphQLOperation,
  executor: () => Promise<T>,
  options?: TelemetryOptions,
): Promise<T> {
  return tracer.startActiveSpan(
    `GraphQL ${operation.operationName || "Operation"}`,
    {
      kind: SpanKind.CLIENT,
      attributes: {
        "graphql.operation.name": operation.operationName || "Unknown",
        "graphql.operation.type": extractOperationType(operation.query),
        "http.method": "POST",
        "http.url": process.env.GRAPHQL_URL || "unknown",
        ...options?.customProperties,
      },
    },
    async (span) => {
      const startTime = Date.now()

      try {
        // Gracefully handle span operations - don't let telemetry errors crash app
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        span.setAttributes =
          span.setAttributes ||
          (() => {
            /* no-op fallback */
          })
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        span.setStatus =
          span.setStatus ||
          (() => {
            /* no-op fallback */
          })
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        span.recordException =
          span.recordException ||
          (() => {
            /* no-op fallback */
          })
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        span.end =
          span.end ||
          (() => {
            /* no-op fallback */
          })
        // Add user context if available
        if (options?.userId) {
          span.setAttributes({
            "user.id": options.userId,
          })
        }

        if (options?.sessionId) {
          span.setAttributes({
            "session.id": options.sessionId,
          })
        }

        const result = await executor()
        const duration = Date.now() - startTime

        span.setAttributes({
          "graphql.operation.duration_ms": duration,
        })

        // Warn about slow operations (> 2 seconds) and track as custom event
        if (duration > 2000) {
          console.warn(
            `🐌 Slow GraphQL operation detected: ${operation.operationName || "Unknown"} took ${duration}ms`,
          )

          // Track slow operation as a custom event
          trackEvent("GraphQL Slow Operation", {
            operationName: operation.operationName || "Unknown",
            duration,
            threshold: 2000,
            query: operation.query.substring(0, 500), // First 500 chars
            variables: JSON.stringify(operation.variables || {}).substring(
              0,
              500,
            ),
            userId: options?.userId,
            sessionId: options?.sessionId,
          })
        }

        span.setStatus({ code: SpanStatusCode.OK })
        return result
      } catch (error) {
        const duration = Date.now() - startTime

        span.setAttributes({
          "graphql.operation.duration_ms": duration,
          "graphql.operation.error": true,
        })

        span.setStatus({
          code: SpanStatusCode.ERROR,
          message: error instanceof Error ? error.message : "Unknown error",
        })
        span.recordException(
          error instanceof Error ? error : new Error(String(error)),
        )
        throw error
      } finally {
        span.end()
      }
    },
  )
}

/**
 * Track custom events with telemetry
 */
export function trackEvent(
  eventName: string,
  properties?: Record<string, any>,
  options?: TelemetryOptions,
): void {
  tracer.startActiveSpan(
    eventName,
    {
      kind: SpanKind.INTERNAL,
      attributes: {
        "event.name": eventName,
        ...properties,
        ...options?.customProperties,
      },
    },
    (span) => {
      if (options?.userId) {
        span.setAttributes({
          "user.id": options.userId,
        })
      }

      if (options?.sessionId) {
        span.setAttributes({
          "session.id": options.sessionId,
        })
      }

      span.setStatus({ code: SpanStatusCode.OK })
      span.end()
    },
  )
}

/**
 * Track API calls with telemetry
 */
export function trackApiCall<T>(
  url: string,
  method: string,
  executor: () => Promise<T>,
  options?: TelemetryOptions,
): Promise<T> {
  return tracer.startActiveSpan(
    `HTTP ${method.toUpperCase()} ${url}`,
    {
      kind: SpanKind.CLIENT,
      attributes: {
        "http.method": method.toUpperCase(),
        "http.url": url,
        component: "http-client",
        ...options?.customProperties,
      },
    },
    async (span) => {
      try {
        if (options?.userId) {
          span.setAttributes({
            "user.id": options.userId,
          })
        }

        if (options?.sessionId) {
          span.setAttributes({
            "session.id": options.sessionId,
          })
        }

        const result = await executor()
        span.setStatus({ code: SpanStatusCode.OK })
        return result
      } catch (error) {
        span.setStatus({
          code: SpanStatusCode.ERROR,
          message: error instanceof Error ? error.message : "Unknown error",
        })
        span.recordException(
          error instanceof Error ? error : new Error(String(error)),
        )
        throw error
      } finally {
        span.end()
      }
    },
  )
}

/**
 * Track page navigation with telemetry
 */
export function trackPageView(
  pageName: string,
  url: string,
  options?: TelemetryOptions,
): void {
  tracer.startActiveSpan(
    `Page View: ${pageName}`,
    {
      kind: SpanKind.INTERNAL,
      attributes: {
        "page.name": pageName,
        "page.url": url,
        "event.type": "page_view",
        ...options?.customProperties,
      },
    },
    (span) => {
      if (options?.userId) {
        span.setAttributes({
          "user.id": options.userId,
        })
      }

      if (options?.sessionId) {
        span.setAttributes({
          "session.id": options.sessionId,
        })
      }

      span.setStatus({ code: SpanStatusCode.OK })
      span.end()
    },
  )
}

/**
 * Track errors with detailed context
 */
export function trackError(
  error: Error,
  context?: string,
  options?: TelemetryOptions,
): void {
  tracer.startActiveSpan(
    `Error: ${context || error.name}`,
    {
      kind: SpanKind.INTERNAL,
      attributes: {
        "error.type": error.name,
        "error.message": error.message,
        "error.context": context || "unknown",
        ...options?.customProperties,
      },
    },
    (span) => {
      if (options?.userId) {
        span.setAttributes({
          "user.id": options.userId,
        })
      }

      if (options?.sessionId) {
        span.setAttributes({
          "session.id": options.sessionId,
        })
      }

      span.setStatus({
        code: SpanStatusCode.ERROR,
        message: error.message,
      })
      span.recordException(error)
      span.end()
    },
  )
}

/**
 * Extract operation type from GraphQL query
 */
function extractOperationType(query: string): string {
  const match = query.trim().match(/^(query|mutation|subscription)/i)
  return match ? match[1].toLowerCase() : "query"
}

/**
 * Get current trace context for correlation
 */
export function getCurrentTraceContext(): any {
  const activeSpan = trace.getActiveSpan()
  if (activeSpan) {
    const spanContext = activeSpan.spanContext()
    return {
      traceId: spanContext.traceId,
      spanId: spanContext.spanId,
    }
  }
  return null
}
